import { createApi } from '@reduxjs/toolkit/query/react';
import { urls } from '@api/urls';
import { PayrollReport } from '@models/Reports';

import { keepDataPerSeconds } from '@redux/constants/queryConfig';
import { baseQuery } from '../baseQuery';
import { TEmployeeContractFinishedResponse } from '@models/PayrollAdjuster';
import {
    getEmployeesWithFinishedContractRequest,
    TAllEmployeesHavePayslipForPeriodResponse,
    TCheckEmployeesHavePayslipByPeriodResponse,
    TCheckEmployerLiquidationDataResponse,
    TPILASpreadSheetPaidResponse,
} from './types';

export const payslipApi = createApi({
    baseQuery,
    reducerPath: 'payslipApi',
    keepUnusedDataFor: keepDataPerSeconds.payslipApi,
    refetchOnReconnect: true,
    endpoints: build => ({
        getPayslipReport: build.query<PayrollReport, string>({
            query: (id: string) => {
                return {
                    url: `${urls.payslip.historical(id)}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: PayrollReport) => response,
        }),

        getEmployeesWithFinishedContract: build.query<TEmployeeContractFinishedResponse, getEmployeesWithFinishedContractRequest>(
            {
                query: (request: getEmployeesWithFinishedContractRequest) => {
                    return {
                        url: `${urls.payslip.getEmployeesWithFinishedContract(request)}`,
                        method: 'GET',
                    };
                },
                transformResponse: (response: TEmployeeContractFinishedResponse) => response,
            }
        ),

        allEmployeesHavePayslipForPeriod: build.query<TAllEmployeesHavePayslipForPeriodResponse, string>({
            query: (periodId: string) => {
                return {
                    url: `${urls.payslip.allEmployeesHavePayslipForPeriod(periodId)}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: TAllEmployeesHavePayslipForPeriodResponse) => response,
        }),

        checkPILASpreadSheetPaid: build.query<TPILASpreadSheetPaidResponse, { employeeId: string; yearPeriodId: string }>({
            query: ({ employeeId, yearPeriodId }) => {
                return {
                    url: `${urls.payslip.checkPILASpreadSheetPaid(employeeId, yearPeriodId)}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: TPILASpreadSheetPaidResponse) => response,
        }),
        checkPILASpreadSheetPaidByEmployer: build.query<
            TPILASpreadSheetPaidResponse,
            { employerId: string; yearPeriodId: string }
        >({
            query: ({ employerId, yearPeriodId }) => {
                return {
                    url: `${urls.payslip.checkPILASpreadSheetPaidByEmployer(employerId, yearPeriodId)}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: TPILASpreadSheetPaidResponse) => response,
        }),

        checkEmployeesHavePayslipByPeriod: build.query<
            TCheckEmployeesHavePayslipByPeriodResponse,
            { periodicityId: string; yearPeriodId: string }
        >({
            query: ({ periodicityId, yearPeriodId }) => {
                return {
                    url: `${urls.payslip.checkEmployeesHavePayslipByPeriod(periodicityId, yearPeriodId)}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: TCheckEmployeesHavePayslipByPeriodResponse) => response,
        }),

        checkEmployerLiquidationData: build.query<TCheckEmployerLiquidationDataResponse, void>({
            query: () => {
                return {
                    url: `${urls.payslip.checkEmployerLiquidationData}`,
                    method: 'GET',
                };
            },
            transformResponse: (response: TCheckEmployerLiquidationDataResponse) => response,
        }),
    }),
});

export const {
    useGetPayslipReportQuery,
    useLazyGetPayslipReportQuery,
    useGetEmployeesWithFinishedContractQuery,
    util,
    useLazyGetEmployeesWithFinishedContractQuery,
    useAllEmployeesHavePayslipForPeriodQuery,
    useLazyAllEmployeesHavePayslipForPeriodQuery,
    useCheckPILASpreadSheetPaidQuery,
    useLazyCheckPILASpreadSheetPaidQuery,
    useCheckPILASpreadSheetPaidByEmployerQuery,
    useCheckEmployeesHavePayslipByPeriodQuery,
    useLazyCheckEmployeesHavePayslipByPeriodQuery,
    useCheckEmployerLiquidationDataQuery,
} = payslipApi;
